import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { CRITERIA_STATUSES, CRITERIA_STATUS_STYLES } from "@/lib/options";
import { LogOut, Search, Filter, Inbox, ExternalLink, FileDown } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";
const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);

const StatCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="border border-slate-200 bg-white p-5">
    <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
    <div className="mt-1 font-mono text-3xl font-medium text-ink">{value}</div>
  </div>
);

const AdminNav = ({ current }) => (
  <div className="flex gap-1">
    <Link to="/admin" data-testid="admin-nav-experiences"
      className={`border-b-2 px-3 py-1.5 text-sm ${current === "exp" ? "border-navy text-ink" : "border-transparent text-slate-500 hover:text-ink"}`}>
      Experiences
    </Link>
    <Link to="/admin/criteria" data-testid="admin-nav-criteria"
      className={`border-b-2 px-3 py-1.5 text-sm ${current === "cri" ? "border-navy text-ink" : "border-transparent text-slate-500 hover:text-ink"}`}>
      Criteria Database
    </Link>
  </div>
);

export default function AdminCriteria() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, verified: 0, rejected: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ status: "Pending", county: "", search: "" });
  const [edit, setEdit] = useState({});

  const buildParams = useCallback(() => {
    const p = {};
    if (filters.status !== ALL) p.status = filters.status;
    if (filters.county) p.county = filters.county;
    if (filters.search) p.search = filters.search;
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api.get("/admin/criteria", { params: buildParams() }),
        api.get("/admin/criteria/stats"),
      ]);
      setRows(list.data);
      setStats(st.data);
    } catch (e) {
      toast.error("Failed to load listings.");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  const openRow = (r) => {
    setSelected(r);
    setEdit({
      listing_agent: r.listing_agent || "", brokerage: r.brokerage || "",
      mls_number: r.mls_number || "", listing_url: r.listing_url || "",
      verified_by: r.verified_by || (user?.name || "Admin"), notes: r.notes || "",
    });
  };

  const saveStatus = async (status) => {
    try {
      await api.patch(`/admin/criteria/${selected.id}`, { ...edit, status });
      toast.success(`Marked ${status}.`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error("Update failed.");
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const downloadDraftReport = async () => {
    toast.info("Generating report…");
    try {
      const res = await fetch(`${API}/admin/report/annual`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "California_Rental_Screening_Report_draft.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Draft report downloaded.");
    } catch (e) {
      toast.error("Report generation failed.");
    }
  };


  return (
    <div className="min-h-screen bg-[#F8FAFC] font-body text-ink">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div>
            <div className="font-serif text-lg font-medium tracking-tight">Research Dashboard</div>
            <div className="mt-2"><AdminNav current="cri" /></div>
          </div>
          <Button data-testid="logout-button" onClick={doLogout} variant="outline" className="rounded-none border-ink">
            <LogOut className="mr-2 h-4 w-4" /> Sign Out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-serif text-xl font-medium text-ink">Criteria Database</h2>
          <Button
            data-testid="draft-report-button"
            onClick={downloadDraftReport}
            className="rounded-none border border-navy bg-transparent font-medium text-navy hover:bg-navy hover:text-white"
          >
            <FileDown className="mr-2 h-4 w-4" /> Generate Annual Report (Draft)
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <StatCard testid="cs-total" label="Total" value={stats.total} />
          <StatCard testid="cs-pending" label="Pending" value={stats.pending} />
          <StatCard testid="cs-verified" label="Verified" value={stats.verified} />
          <StatCard testid="cs-rejected" label="Rejected" value={stats.rejected} />
          <StatCard testid="cs-archived" label="Archived" value={stats.archived} />
        </div>

        <div className="mt-8 border border-slate-200 bg-white p-5">
          <div className="mb-4 flex items-center gap-2 text-sm font-medium uppercase tracking-widest text-slate-500">
            <Filter className="h-4 w-4" /> Filters
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input data-testid="cr-search" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                placeholder="Search city / ZIP…" className="rounded-none border-slate-200 bg-white pl-9" />
            </div>
            <Input data-testid="cr-county-filter" value={filters.county} onChange={(e) => setFilters((f) => ({ ...f, county: e.target.value }))}
              placeholder="County" className="rounded-none border-slate-200 bg-white" />
            <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="cr-status-filter" className="rounded-none border-slate-200 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {CRITERIA_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-6 text-sm text-slate-500" data-testid="cr-result-count">{rows.length} listing(s)</div>

        <div className="mt-3 overflow-x-auto border border-slate-200 bg-white">
          <table className="w-full border-collapse text-sm" data-testid="criteria-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">City / County</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Rent</th>
                <th className="px-4 py-3">Credit</th>
                <th className="px-4 py-3">Income</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-400"><Inbox className="mx-auto mb-3 h-10 w-10" />No listings match.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} data-testid={`criteria-row-${r.id}`} onClick={() => openRow(r)}
                  className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</td>
                  <td className="px-4 py-3 font-medium">{r.city}, {r.county}</td>
                  <td className="px-4 py-3">{r.property_type}</td>
                  <td className="px-4 py-3 font-mono">{money(r.rent)}</td>
                  <td className="px-4 py-3 font-mono">{r.min_credit_score ?? "—"}</td>
                  <td className="px-4 py-3 font-mono">{r.income_requirement}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={`rounded-none border ${CRITERIA_STATUS_STYLES[r.status]}`}>{r.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none border-slate-200" data-testid="criteria-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-medium tracking-tight">Verify Listing</DialogTitle>
                <DialogDescription className="text-slate-500">Review advertised criteria and set a verification status.</DialogDescription>
              </DialogHeader>
              <div className="space-y-6 text-sm">
                <div className="grid grid-cols-2 gap-4 border border-slate-200 bg-[#F8FAFC] p-4">
                  <Info label="Rent" value={money(selected.rent)} />
                  <Info label="Type" value={selected.property_type} />
                  <Info label="City / County" value={`${selected.city}, ${selected.county}`} />
                  <Info label="ZIP" value={selected.zip_code || "—"} />
                  <Info label="Beds / Baths" value={`${selected.bedrooms ?? "—"} / ${selected.bathrooms ?? "—"}`} />
                  <Info label="Min Credit" value={selected.min_credit_score ?? "—"} />
                  <Info label="Income Req." value={`${selected.income_requirement}${selected.income_custom ? ` (${selected.income_custom}×)` : ""}`} />
                  <Info label="Cosigners" value={selected.cosigners} />
                  <Info label="Reserves" value={selected.reserves_required ? `Yes${selected.reserves_months ? ` (${selected.reserves_months} mo)` : ""}` : "No"} />
                  <Info label="Application Fee" value={money(selected.application_fee)} />
                  <Info label="Background Check" value={selected.background_check == null ? "—" : selected.background_check ? "Yes" : "No"} />
                  <Info label="Pets Allowed" value={selected.pets_allowed == null ? "—" : selected.pets_allowed ? "Yes" : "No"} />
                </div>

                {selected.listing_url && (
                  <a href={selected.listing_url} target="_blank" rel="noreferrer" data-testid="listing-source-link"
                    className="inline-flex items-center gap-1 text-calblue hover:underline">
                    <ExternalLink className="h-4 w-4" /> Open source listing
                  </a>
                )}

                <div className="space-y-4 border-t border-slate-200 pt-5">
                  <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Verification (admin only)</div>
                  <div className="grid grid-cols-2 gap-4">
                    <LabeledInput label="Listing Agent" value={edit.listing_agent} onChange={(v) => setEdit((e) => ({ ...e, listing_agent: v }))} />
                    <LabeledInput label="Brokerage" value={edit.brokerage} onChange={(v) => setEdit((e) => ({ ...e, brokerage: v }))} />
                    <LabeledInput label="MLS Number" value={edit.mls_number} onChange={(v) => setEdit((e) => ({ ...e, mls_number: v }))} />
                    <LabeledInput label="Verified By" value={edit.verified_by} onChange={(v) => setEdit((e) => ({ ...e, verified_by: v }))} />
                  </div>
                  <LabeledInput label="Notes" value={edit.notes} onChange={(v) => setEdit((e) => ({ ...e, notes: v }))} />
                </div>

                <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-5">
                  <Button data-testid="verify-button" onClick={() => saveStatus("Verified")} className="rounded-none bg-navy text-white hover:bg-navy-hover">Mark Verified</Button>
                  <Button data-testid="reject-button" variant="outline" onClick={() => saveStatus("Rejected")} className="rounded-none border-red-300 text-red-700">Reject</Button>
                  <Button data-testid="archive-button" variant="outline" onClick={() => saveStatus("Archived")} className="rounded-none border-slate-300 text-slate-600">Archive</Button>
                  <Button variant="ghost" onClick={() => saveStatus("Pending")} className="rounded-none text-slate-500">Keep Pending</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
    <div className="font-medium text-ink">{value}</div>
  </div>
);

const LabeledInput = ({ label, value, onChange }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
    <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 rounded-none border-slate-200" />
  </div>
);
